// frontend/packages/business-console/src/lib/outboxStore.js
//
// Work item 2.19 — IndexedDB-backed outbox for offline-tolerant draft
// saves. Design source: `docs/design/quotes-invoices-console.md` §7a.
//
// Schema (db `upryzze-console`, object store `outbox`):
//
//   {
//     clientUuid:  string,       // primary key; UUIDv4 assigned at
//                                // editor-open time, reused on retries
//                                // so the server idempotency middleware
//                                // dedupes replays.
//     kind:        'quote' | 'invoice' | 'workflow-form',
//                                // 'workflow-form' added in 5.2 — the
//                                // hook is generic over kind, so the
//                                // store schema is unchanged; the
//                                // (kind, status) index already covers
//                                // the new value.
//     route:       string,       // 'POST /api/v2/crm/quotes' etc.
//     payload:     object,       // body sent to the route
//     savedAt:     number,       // ms epoch — last local mutation
//     status:      'pending' | 'syncing' | 'synced' | 'failed',
//     attemptCount:number,
//     lastError:   string|null,  // last failure message (4xx -> failed)
//     serverId:    string|null,  // hydrated after a successful sync
//     serverNumber:string|null,  // hydrated after a successful sync
//   }
//
// Hard-no-dependency policy: we do not pull `idb` or `uuid`. Native
// IndexedDB + `crypto.randomUUID()` cover this. CLAUDE.md "Adding
// dependencies" rule — every new npm dep blocks deploy until the
// lockfile is regenerated locally; not worth it here.

const DB_NAME = 'upryzze-console';
// Bumped to v2 by 5.9 to add the `customerCache` store. The
// onupgradeneeded handler creates both stores conditionally so
// either entry point (outbox or customerCache) can open the DB
// from a cold start without depending on the other being loaded
// first. The schemas don't interact.
const DB_VERSION = 2;
const STORE = 'outbox';
const CUSTOMER_STORE = 'customerCache';

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  if (typeof indexedDB === 'undefined') {
    // Not in a browser (SSR / test). Return a stub that all calls
    // reject through so callers degrade to no-op.
    return Promise.reject(new Error('IndexedDB not available'));
  }
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, {
          keyPath: 'clientUuid',
        });
        store.createIndex('by_kind_status', ['kind', 'status']);
        store.createIndex('by_kind', 'kind');
        store.createIndex('by_savedAt', 'savedAt');
      }
      // 5.9 — co-create the customer-cache store. Either entry
      // point (outbox or customerCache) can open the DB cold.
      if (!db.objectStoreNames.contains(CUSTOMER_STORE)) {
        const cs = db.createObjectStore(CUSTOMER_STORE, {
          keyPath: 'customerId',
        });
        cs.createIndex('by_cachedAt', 'cachedAt');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(db, mode) {
  return db.transaction(STORE, mode).objectStore(STORE);
}

function wrap(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function newClientUuid() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // Fallback for ancient browsers (RFC4122 v4-ish).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export async function put(item) {
  try {
    const db = await openDb();
    await wrap(tx(db, 'readwrite').put(item));
    return item;
  } catch {
    return item;
  }
}

export async function get(clientUuid) {
  try {
    const db = await openDb();
    return (await wrap(tx(db, 'readonly').get(clientUuid))) || null;
  } catch {
    return null;
  }
}

export async function remove(clientUuid) {
  try {
    const db = await openDb();
    await wrap(tx(db, 'readwrite').delete(clientUuid));
  } catch {
    /* ignore */
  }
}

export async function listAll({ kind } = {}) {
  try {
    const db = await openDb();
    const store = tx(db, 'readonly');
    const items = await wrap(
      kind ? store.index('by_kind').getAll(kind) : store.getAll()
    );
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
}

export async function listByStatus({ kind, status }) {
  const all = await listAll({ kind });
  if (!status) return all;
  const wanted = Array.isArray(status) ? status : [status];
  return all.filter((it) => wanted.includes(it.status));
}

export async function pruneSynced({ olderThanMs = 5 * 60 * 1000 } = {}) {
  try {
    const cutoff = Date.now() - olderThanMs;
    const all = await listAll();
    await Promise.all(
      all
        .filter((it) => it.status === 'synced' && (it.savedAt || 0) < cutoff)
        .map((it) => remove(it.clientUuid))
    );
  } catch {
    /* ignore */
  }
}

// Test-only escape hatch: dump the store + reset the cached connection so
// jest-environment-jsdom or storybook can wipe state between cases. Not
// used in production runtime.
export async function __resetForTests() {
  try {
    if (dbPromise) {
      const db = await dbPromise;
      db.close();
    }
  } catch {
    /* ignore */
  }
  dbPromise = null;
  if (typeof indexedDB !== 'undefined' && indexedDB.deleteDatabase) {
    await new Promise((resolve) => {
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = req.onerror = req.onblocked = resolve;
    });
  }
}
